// src/components/CiscoTerminal.jsx
// Terminal Cisco IOS simulé — mode moyen
import React, { useState, useRef, useEffect, useCallback } from "react";
import { Box, Typography } from "@mui/material";

// ── Palette terminal ──────────────────────────────────────────────────────────
const T = {
  bg:      "#0d1117",
  text:    "#e6edf3",
  prompt:  "#58a6ff",
  green:   "#3fb950",
  red:     "#f85149",
  yellow:  "#d29922",
  gray:    "#8b949e",
  blue:    "#79c0ff",
  border:  "#21262d",
};

// ── Moteur IOS côté client (simulation partielle) ────────────────────────────
function createDevice(hostname = "Router") {
  return {
    hostname,
    mode: "user",          // user | enable | config | interface | router-ospf | router-rip | line-vty
    context: null,         // interface ou router courant
    interfaces: {},        // { "GigabitEthernet0/0": { ip, mask, status, desc } }
    routes: [],            // routes statiques
    ospf: null,            // { pid, networks[] }
    rip: null,
    hostname_val: hostname,
    enable_pass: "cisco",
    lines: { vty: { password: "", login: false } },
    running_config: [],
  };
}

// Résoudre le prompt selon le mode
function getPrompt(dev) {
  const h = dev.hostname_val || dev.hostname;
  switch (dev.mode) {
    case "user":          return `${h}>`;
    case "enable":        return `${h}#`;
    case "config":        return `${h}(config)#`;
    case "interface":     return `${h}(config-if)#`;
    case "router-ospf":   return `${h}(config-router)#`;
    case "router-rip":    return `${h}(config-router)#`;
    case "line-vty":      return `${h}(config-line)#`;
    default:              return `${h}#`;
  }
}

// Normaliser le nom d'interface
function normalizeIface(s) {
  return s
    .replace(/^gi(gabit)?(\s*ethernet)?/i, "GigabitEthernet")
    .replace(/^fa(st)?(\s*ethernet)?/i,    "FastEthernet")
    .replace(/^se(rial)?/i,                "Serial")
    .replace(/^lo(opback)?/i,              "Loopback")
    .replace(/\s+/g, "");
}

// Formater show ip interface brief
function showIpIntBrief(dev) {
  const lines = [
    "Interface              IP-Address      OK? Method Status                Protocol",
  ];
  Object.entries(dev.interfaces).forEach(([name, cfg]) => {
    const ip     = cfg.ip   || "unassigned";
    const ok     = cfg.ip   ? "YES" : "NO";
    const status = cfg.up   ? "up"  : "administratively down";
    const proto  = cfg.up && cfg.ip ? "up" : "down";
    lines.push(
      `${name.padEnd(23)}${ip.padEnd(16)}${ok}    manual ${status.padEnd(22)}${proto}`
    );
  });
  return lines;
}

// Formater show running-config
function showRunning(dev) {
  const lines = [
    "Building configuration...", "",
    "Current configuration :", "!",
    `hostname ${dev.hostname_val}`, "!",
  ];
  if (dev.enable_pass !== "cisco")
    lines.push(`enable password ${dev.enable_pass}`, "!");
  Object.entries(dev.interfaces).forEach(([name, cfg]) => {
    lines.push(`interface ${name}`);
    if (cfg.desc)        lines.push(` description ${cfg.desc}`);
    if (cfg.ip)          lines.push(` ip address ${cfg.ip} ${cfg.mask}`);
    if (!cfg.up)         lines.push(" shutdown");
    else                 lines.push(" no shutdown");
    lines.push("!");
  });
  if (dev.ospf) {
    lines.push(`router ospf ${dev.ospf.pid}`);
    (dev.ospf.networks || []).forEach(n => lines.push(` network ${n}`));
    lines.push("!");
  }
  if (dev.rip) {
    lines.push("router rip");
    lines.push(` version ${dev.rip.version || 2}`);
    (dev.rip.networks || []).forEach(n => lines.push(` network ${n}`));
    lines.push("!");
  }
  dev.routes.forEach(r => lines.push(`ip route ${r}`));
  if (dev.routes.length) lines.push("!");
  lines.push("end");
  return lines;
}

// Simuler un ping
function simulatePing(dev, target) {
  // Heuristique : si une interface a une ip dans le même sous-réseau → succès
  const success = Object.values(dev.interfaces).some(ifc => {
    if (!ifc.ip || !ifc.up) return false;
    const parts = ifc.ip.split(".");
    const tParts = target.split(".");
    return parts[0] === tParts[0] && parts[1] === tParts[1] && parts[2] === tParts[2];
  });
  const lines = [
    `Type escape sequence to abort.`,
    `Sending 5, 100-byte ICMP Echos to ${target}, timeout is 2 seconds:`,
    success ? "!!!!!" : ".....",
    `Success rate is ${success ? 100 : 0} percent (${success ? "5/5" : "0/5"}), `
      + `round-trip min/avg/max = ${success ? "1/2/4" : "-/-/-"} ms`,
  ];
  return lines;
}

// ── Interpréteur de commandes ─────────────────────────────────────────────────
function execCommand(dev, raw) {
  const setDev = d => d; // on retournera un nouveau state
  const cmd = raw.trim();
  if (!cmd) return { dev, output: [] };

  const lower = cmd.toLowerCase();
  const tokens = cmd.split(/\s+/);
  const t0 = lower.split(/\s+/)[0];

  // ── Commandes globales ────────────────────────────────────────────────────
  if (lower === "?" || lower === "help") {
    return { dev, output: ["  enable         Turn on privileged commands",
      "  show            Show running system information",
      "  ping            Send echo messages",
      "  configure       Enter configuration mode",
      "  disable         Turn off privileged commands",
      "  exit / end      Exit current mode",
    ]};
  }

  if (lower === "enable") {
    if (dev.mode !== "user") return { dev, output: ["% Already in enable mode"] };
    const newDev = { ...dev, mode: "enable" };
    return { dev: newDev, output: [] };
  }

  if (lower === "disable") {
    return { dev: { ...dev, mode: "user" }, output: [] };
  }

  if (lower === "end" || lower === "^z") {
    if (["config","interface","router-ospf","router-rip","line-vty"].includes(dev.mode))
      return { dev: { ...dev, mode: "enable", context: null }, output: [] };
    return { dev, output: [] };
  }

  if (lower === "exit") {
    if (dev.mode === "interface" || dev.mode === "router-ospf" || dev.mode === "router-rip" || dev.mode === "line-vty")
      return { dev: { ...dev, mode: "config", context: null }, output: [] };
    if (dev.mode === "config")
      return { dev: { ...dev, mode: "enable" }, output: [] };
    if (dev.mode === "enable")
      return { dev: { ...dev, mode: "user" }, output: [] };
    return { dev, output: [] };
  }

  // ── configure terminal ────────────────────────────────────────────────────
  if (lower.startsWith("conf") && (lower.includes("term") || tokens.length === 1)) {
    if (dev.mode !== "enable")
      return { dev, output: ["% Command not available in this mode"] };
    return { dev: { ...dev, mode: "config" }, output: ["Enter configuration commands, one per line.  End with CNTL/Z."] };
  }

  // ── hostname ──────────────────────────────────────────────────────────────
  if (t0 === "hostname" && dev.mode === "config") {
    const name = tokens[1] || dev.hostname;
    return { dev: { ...dev, hostname_val: name }, output: [] };
  }

  // ── enable password ───────────────────────────────────────────────────────
  if (lower.startsWith("enable password") && dev.mode === "config") {
    const pass = tokens[2] || "";
    return { dev: { ...dev, enable_pass: pass }, output: [] };
  }

  // ── interface ─────────────────────────────────────────────────────────────
  if (t0 === "interface" && dev.mode === "config") {
    const ifaceName = normalizeIface(tokens.slice(1).join(" "));
    const ifaces = { ...dev.interfaces };
    if (!ifaces[ifaceName]) ifaces[ifaceName] = { ip: "", mask: "", up: false, desc: "" };
    return { dev: { ...dev, mode: "interface", context: ifaceName, interfaces: ifaces }, output: [] };
  }

  // ── ip address ────────────────────────────────────────────────────────────
  if (lower.startsWith("ip address") && dev.mode === "interface") {
    if (lower.includes("dhcp")) {
      const ifaces = { ...dev.interfaces, [dev.context]: { ...dev.interfaces[dev.context], ip: "DHCP", mask: "" } };
      return { dev: { ...dev, interfaces: ifaces }, output: [] };
    }
    const ip = tokens[2]; const mask = tokens[3];
    if (!ip || !mask) return { dev, output: ["% Incomplete command"] };
    const ifaces = { ...dev.interfaces, [dev.context]: { ...dev.interfaces[dev.context], ip, mask } };
    return { dev: { ...dev, interfaces: ifaces }, output: [] };
  }

  // ── no ip address ─────────────────────────────────────────────────────────
  if (lower.startsWith("no ip address") && dev.mode === "interface") {
    const ifaces = { ...dev.interfaces, [dev.context]: { ...dev.interfaces[dev.context], ip: "", mask: "" } };
    return { dev: { ...dev, interfaces: ifaces }, output: [] };
  }

  // ── no shutdown / shutdown ────────────────────────────────────────────────
  if (lower === "no shutdown" && dev.mode === "interface") {
    const ifaces = { ...dev.interfaces, [dev.context]: { ...dev.interfaces[dev.context], up: true } };
    return { dev: { ...dev, interfaces: ifaces }, output: [
      `%LINK-5-CHANGED: Interface ${dev.context}, changed state to up`,
      `%LINEPROTO-5-UPDOWN: Line protocol on Interface ${dev.context}, changed state to up`,
    ]};
  }
  if (lower === "shutdown" && dev.mode === "interface") {
    const ifaces = { ...dev.interfaces, [dev.context]: { ...dev.interfaces[dev.context], up: false } };
    return { dev: { ...dev, interfaces: ifaces }, output: [
      `%LINK-5-CHANGED: Interface ${dev.context}, changed state to administratively down`,
    ]};
  }

  // ── description ───────────────────────────────────────────────────────────
  if (t0 === "description" && dev.mode === "interface") {
    const desc = tokens.slice(1).join(" ");
    const ifaces = { ...dev.interfaces, [dev.context]: { ...dev.interfaces[dev.context], desc } };
    return { dev: { ...dev, interfaces: ifaces }, output: [] };
  }

  // ── router ospf ───────────────────────────────────────────────────────────
  if (lower.startsWith("router ospf") && dev.mode === "config") {
    const pid = parseInt(tokens[2]) || 1;
    const ospf = dev.ospf || { pid, networks: [] };
    return { dev: { ...dev, mode: "router-ospf", context: "ospf", ospf: { ...ospf, pid } }, output: [] };
  }

  // ── router rip ────────────────────────────────────────────────────────────
  if (lower.startsWith("router rip") && dev.mode === "config") {
    const rip = dev.rip || { version: 2, networks: [] };
    return { dev: { ...dev, mode: "router-rip", context: "rip", rip }, output: [] };
  }

  // ── version (inside router rip) ───────────────────────────────────────────
  if (t0 === "version" && dev.mode === "router-rip") {
    const ver = parseInt(tokens[1]) || 2;
    return { dev: { ...dev, rip: { ...dev.rip, version: ver } }, output: [] };
  }

  // ── network (inside router) ───────────────────────────────────────────────
  if (t0 === "network" && (dev.mode === "router-ospf" || dev.mode === "router-rip")) {
    const net = tokens.slice(1).join(" ");
    if (dev.mode === "router-ospf") {
      const networks = [...(dev.ospf?.networks || []), net];
      return { dev: { ...dev, ospf: { ...dev.ospf, networks } }, output: [] };
    } else {
      const networks = [...(dev.rip?.networks || []), net];
      return { dev: { ...dev, rip: { ...dev.rip, networks } }, output: [] };
    }
  }

  // ── ip route ──────────────────────────────────────────────────────────────
  if (lower.startsWith("ip route") && dev.mode === "config") {
    const route = tokens.slice(2).join(" ");
    return { dev: { ...dev, routes: [...dev.routes, route] }, output: [] };
  }

  // ── line vty ──────────────────────────────────────────────────────────────
  if (lower.startsWith("line vty") && dev.mode === "config") {
    return { dev: { ...dev, mode: "line-vty" }, output: [] };
  }
  if (t0 === "password" && dev.mode === "line-vty") {
    return { dev: { ...dev, lines: { ...dev.lines, vty: { ...dev.lines.vty, password: tokens[1] } } }, output: [] };
  }
  if (lower === "login" && dev.mode === "line-vty") {
    return { dev: { ...dev, lines: { ...dev.lines, vty: { ...dev.lines.vty, login: true } } }, output: [] };
  }

  // ── show commands ─────────────────────────────────────────────────────────
  if (t0 === "show" || (lower.startsWith("do show") && dev.mode !== "user" && dev.mode !== "enable")) {
    const showTokens = lower.startsWith("do ") ? lower.slice(3).split(/\s+/) : lower.split(/\s+/);
    const s1 = showTokens[1]; const s2 = showTokens[2]; const s3 = showTokens[3];

    if (s1 === "version") return { dev, output: [
      "Cisco IOS Software, Version 15.6(1)T (Simulation)",
      "ROM: Bootstrap program is IOSv",
      `Uptime is 0 minutes`,
      "System image file is flash:vios-adventerprisek9-m",
      `${dev.hostname_val} uptime is 0 minutes`,
    ]};

    if (s1 === "ip" && s2 === "interface" && s3 === "brief")
      return { dev, output: showIpIntBrief(dev) };

    if (s1 === "ip" && s2 === "interface")
      return { dev, output: showIpIntBrief(dev) };

    if (s1 === "interfaces")
      return { dev, output: showIpIntBrief(dev) };

    if (s1 === "running-config" || (s1 === "run"))
      return { dev, output: showRunning(dev) };

    if (s1 === "startup-config")
      return { dev, output: ["startup-config is not present"] };

    if (s1 === "ip" && s2 === "route") return { dev, output: [
      "Codes: L - local, C - connected, S - static, R - RIP, O - OSPF",
      "",
      ...Object.entries(dev.interfaces)
        .filter(([,c]) => c.ip && c.up)
        .map(([n, c]) => `C    ${c.ip.split(".").slice(0,3).join(".")}.0/24 is directly connected, ${n}`),
      ...dev.routes.map(r => `S    ${r}`),
    ]};

    if (s1 === "ip" && s2 === "ospf")
      return { dev, output: dev.ospf
        ? [`Routing Process "ospf ${dev.ospf.pid}" with ID ${dev.ospf.pid}.1.1.1`,
           `Start time: 00:00:01.000, Time elapsed: 00:01:00.000`,
           `Supports only single TOS(TOS0) routes`,
           `Supports opaque LSA`,
           `Number of areas in this router is 1. 1 normal 0 stub 0 nssa`,
           ...( dev.ospf.networks.map(n => `  Network ${n}`) ),
          ]
        : ["% OSPF is not configured"] };

    if (s1 === "ip" && s2 === "protocols") return { dev, output: [
      "Routing Protocol is \"ospf " + (dev.ospf?.pid ?? "-") + "\"",
      "  Outgoing update filter list for all interfaces is not set",
      "  Incoming update filter list for all interfaces is not set",
    ]};

    if (s1 === "cdp" || s1 === "arp")
      return { dev, output: ["% No entries"] };

    return { dev, output: [`% Unknown show command: show ${showTokens.slice(1).join(" ")}`] };
  }

  // ── ping ──────────────────────────────────────────────────────────────────
  if (t0 === "ping") {
    const target = tokens[1];
    if (!target) return { dev, output: ["% Incomplete command — Usage: ping <ip>"] };
    return { dev, output: simulatePing(dev, target) };
  }

  // ── copy running-config startup-config ────────────────────────────────────
  if (lower.includes("copy") && lower.includes("running") && lower.includes("startup")) {
    return { dev, output: ["Destination filename [startup-config]? ", "Building configuration...", "[OK]"] };
  }

  // ── write memory ──────────────────────────────────────────────────────────
  if (lower === "wr" || lower === "write memory" || lower === "write") {
    return { dev, output: ["Building configuration...", "[OK]"] };
  }

  // ── clear ─────────────────────────────────────────────────────────────────
  if (lower === "clear") return { dev, output: ["__CLEAR__"] };

  // ── Commande inconnue ─────────────────────────────────────────────────────
  return { dev, output: [`% Invalid input detected at '^' marker.`, `         ^`, `% Unknown command: ${cmd}`] };
}

// ── Composant terminal ────────────────────────────────────────────────────────
export default function CiscoTerminal({
  deviceId = "R1",
  initialHostname = "Router",
  initialConfig = null,   // objet device pré-configuré
  onStateChange = null,   // callback(deviceState) à chaque commande
  height = 400,
  readOnly = false,
  title = null,
}) {
  const [device,  setDevice]  = useState(() => initialConfig || createDevice(initialHostname));
  const [history, setHistory] = useState([
    { type: "system", text: `Connected to ${deviceId} — Cisco IOS Simulation` },
    { type: "system", text: 'Type "?" for help. Type "enable" then "configure terminal" to configure.' },
    { type: "system", text: "" },
  ]);
  const [input,   setInput]   = useState("");
  const [cmdHist, setCmdHist] = useState([]);
  const [histIdx, setHistIdx] = useState(-1);
  const inputRef = useRef();
  const bottomRef = useRef();

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [history]);

  const submit = useCallback(() => {
    if (readOnly) return;
    const cmd = input.trim();
    const prompt = getPrompt(device);

    // Ajouter la ligne de commande dans l'historique affiché
    const newHistory = [...history, { type: "input", text: `${prompt} ${cmd}` }];

    // Exécuter
    const { dev: newDev, output } = execCommand(device, cmd);

    // Clear spécial
    if (output[0] === "__CLEAR__") {
      setHistory([]);
      setInput("");
      setDevice(newDev);
      onStateChange?.(newDev);
      return;
    }

    // Ajouter les lignes de sortie
    output.forEach(line => newHistory.push({ type: "output", text: line }));

    setHistory(newHistory);
    setDevice(newDev);
    setInput("");
    onStateChange?.(newDev);

    // Historique navigation
    if (cmd) {
      setCmdHist(prev => [cmd, ...prev.slice(0, 49)]);
      setHistIdx(-1);
    }
  }, [input, device, history, readOnly, onStateChange]);

  const onKeyDown = (e) => {
    if (e.key === "Enter") { e.preventDefault(); submit(); return; }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      const newIdx = Math.min(histIdx + 1, cmdHist.length - 1);
      setHistIdx(newIdx);
      setInput(cmdHist[newIdx] || "");
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      const newIdx = Math.max(histIdx - 1, -1);
      setHistIdx(newIdx);
      setInput(newIdx === -1 ? "" : (cmdHist[newIdx] || ""));
    }
    // Ctrl+C
    if (e.ctrlKey && e.key === "c") {
      setHistory(prev => [...prev, { type: "input", text: `${getPrompt(device)} ^C` }]);
      setInput("");
    }
  };

  const lineColor = (type) => {
    if (type === "system") return T.gray;
    if (type === "input")  return T.prompt;
    return T.text;
  };

  return (
    <Box sx={{
      background: T.bg,
      borderRadius: "10px",
      border: `1px solid ${T.border}`,
      overflow: "hidden",
      fontFamily: "'Courier New', 'Lucida Console', monospace",
      height,
      display: "flex",
      flexDirection: "column",
    }}>
      {/* Barre titre */}
      <Box sx={{
        background: "#161b22",
        borderBottom: `1px solid ${T.border}`,
        px: 2, py: 1,
        display: "flex", alignItems: "center", gap: 1,
      }}>
        <Box sx={{ width: 10, height: 10, borderRadius: "50%", background: "#ff5f57" }} />
        <Box sx={{ width: 10, height: 10, borderRadius: "50%", background: "#febc2e" }} />
        <Box sx={{ width: 10, height: 10, borderRadius: "50%", background: "#28c840" }} />
        <Typography sx={{ ml: 1.5, fontSize: "0.78rem", color: T.gray, fontFamily: "inherit" }}>
          {title || `${deviceId} — Cisco IOS`}
        </Typography>
      </Box>

      {/* Corps terminal */}
      <Box
        sx={{ flex: 1, overflowY: "auto", p: 1.5, cursor: "text" }}
        onClick={() => inputRef.current?.focus()}
      >
        {history.map((line, i) => (
          <Typography key={i} sx={{
            fontSize: "0.82rem",
            fontFamily: "inherit",
            color: lineColor(line.type),
            lineHeight: 1.6,
            whiteSpace: "pre-wrap",
            wordBreak: "break-all",
          }}>
            {line.text}
          </Typography>
        ))}

        {/* Ligne d'entrée active */}
        {!readOnly && (
          <Box sx={{ display: "flex", alignItems: "center", mt: 0.25 }}>
            <Typography sx={{ fontSize: "0.82rem", fontFamily: "inherit", color: T.prompt, flexShrink: 0 }}>
              {getPrompt(device)}&nbsp;
            </Typography>
            <Box
              component="input"
              ref={inputRef}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              autoFocus
              spellCheck={false}
              autoComplete="off"
              sx={{
                flex: 1,
                background: "transparent",
                border: "none",
                outline: "none",
                color: T.text,
                fontSize: "0.82rem",
                fontFamily: "inherit",
                caretColor: T.green,
              }}
            />
          </Box>
        )}
        <div ref={bottomRef} />
      </Box>
    </Box>
  );
}

export { createDevice, execCommand, getPrompt };